import {
  AuthenticatedUserContextType,
  useUserContext,
} from '@/contexts/user-context';
import { useMemo } from 'react';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

/**
 * Check if commission is allowed for any supplier associated with the user
 * @param supplierEmployeeMap
 * @returns True if at least one supplier has allow_commission enabled
 */
const isCommissionAllowedForAnySupplier = (
  supplierEmployeeMap?: AuthenticatedUserContextType['supplierEmployeeMap'],
): boolean => {
  if (!supplierEmployeeMap) {
    return false;
  }
  return Object.values(supplierEmployeeMap).some(
    (supplierEmployee) =>
      supplierEmployee?.employee_permissions?.allow_commission,
  );
};

/**
 * Custom hook to determine if the current user can view monthly commission data.
 *
 * This hook performs two key validations to control access to monthly commission features:
 *
 * **1. Dynamic Config Validation:**
 * - Retrieves the `SalesRepConfig.CommissionSettings` from the Supplier level (not chat-level)
 * - Checks if `monthly_commission_enabled` flag is set to true in the supplier's dynamic configuration
 *
 * **2. User Permissions Validation:**
 * - Retrieves `BusinessEmployee.EmployeePermissions.allow_commission` settings
 * - Checks if ANY supplier associated with the business organization has commission permissions enabled
 * - Also verifies if ANY business employee related to the organization has commission permissions enabled
 *
 * **Permission Logic:**
 * Since this feature is indexed by Business Org Id, access is granted if:
 * - At least ONE supplier in the business org has the commission setting enabled, AND
 * - At least ONE business employee has the `allow_commission` permission enabled, AND
 * - The monthly commission feature is enabled in the supplier's dynamic config
 *
 * @returns {boolean} `true` if the user can view monthly commission data, `false` otherwise
 */
export const useCanViewMonthlyCommission = () => {
  const userContext = useUserContext();
  // use supplier dynamic config rather than chat-level one.
  const salesRepConfig = useSupplierDynamicConfigValue('sales_rep_config');
  const isMonthlyCommissionEnabled =
    salesRepConfig?.commission_settings?.monthly_commission_enabled ?? false;

  const isCommissionAllowed = useMemo(
    () =>
      isCommissionAllowedForAnySupplier(
        'supplierEmployeeMap' in userContext
          ? userContext.supplierEmployeeMap
          : undefined,
      ),
    [userContext],
  );
  return isCommissionAllowed && isMonthlyCommissionEnabled;
};
